import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { API_BASE, BUSINESS_INFO } from "../config";
import bannerLarge from "../img/banner-1600.jpg";
import bannerSmall from "../img/banner-900.jpg";
import { trackContactClick } from "../utils/analytics";
import {
  getOptimizedImageUrl,
  getResponsiveImageSrcSet,
  normalizeImageUrl,
} from "../utils/imageUrl";

const DEFAULT_BANNER = {
  image: bannerLarge,
  imageSmall: bannerSmall,
  title: `${BUSINESS_INFO.name} - Giường bọc nệm chuẩn gu`,
  subtitle:
    "Thiết kế tinh tế, chất liệu phù hợp, nội thất đồng bộ giúp tổ ấm thêm xinh. Giao tận nơi, hậu mãi chu đáo, tư vấn miễn phí.",
  buttonText: "Xem bộ sưu tập",
  buttonLink: "#products",
};

function cleanBannerText(value) {
  return String(value || "")
    .replace(/\s*\bcao cấp\b\s*/giu, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeBanner(banner) {
  if (!banner) return DEFAULT_BANNER;

  const isCurrentGithubBanner =
    typeof banner.image === "string" &&
    banner.image.includes("github.com/cunghome21/sofa-image") &&
    banner.image.includes("/banners/banner1.jpg");

  return {
    ...DEFAULT_BANNER,
    ...banner,
    title: cleanBannerText(banner.title) || DEFAULT_BANNER.title,
    subtitle: cleanBannerText(banner.subtitle) || DEFAULT_BANNER.subtitle,
    buttonText: cleanBannerText(banner.buttonText) || DEFAULT_BANNER.buttonText,
    buttonLink: String(banner.buttonLink || "").trim() || DEFAULT_BANNER.buttonLink,
    image: isCurrentGithubBanner ? bannerLarge : normalizeImageUrl(banner.image) || bannerLarge,
    imageSmall: isCurrentGithubBanner
      ? bannerSmall
      : normalizeImageUrl(banner.imageSmall || banner.image) || bannerSmall,
  };
}

export default function BannerCarousel() {
  const [banners, setBanners] = useState([DEFAULT_BANNER]);
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/banners`)
      .then((res) => {
        if (!res.ok) throw new Error(`Banner fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const normalized = Array.isArray(data) ? data.map(normalizeBanner) : [];
        if (normalized.length > 0) {
          setBanners(normalized);
          setIndex(0);
        }
      })
      .catch((err) => console.error("Error loading banners:", err));
  }, []);

  // Auto slide ảnh
  useEffect(() => {
    if (hovered || banners.length === 0) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [hovered, banners]);

  const handlePrimaryCta = (event) => {
    const target = banners[index]?.buttonLink || DEFAULT_BANNER.buttonLink;

    if (!target.startsWith("#")) return;

    const section = document.querySelector(target);
    if (!section) return;

    event.preventDefault();
    section.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", target);
  };

  return (
    <section
      id="home"
      className="relative min-h-[90vh] flex items-center overflow-hidden scroll-mt-24 md:scroll-mt-32"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Ảnh nền full */}
      <AnimatePresence mode="wait">
        {banners.length > 0 && (
          <motion.img
            key={banners[index]?.image}
            src={getOptimizedImageUrl(banners[index]?.image, { width: 1600, quality: "auto:good" })}
            srcSet={
              getResponsiveImageSrcSet(
                banners[index]?.image,
                [640, 900, 1280, 1600],
                { quality: "auto:good" }
              ) ||
              `${banners[index]?.imageSmall || banners[index]?.image} 900w, ${banners[index]?.image} 1600w`
            }
            sizes="100vw"
            alt={banners[index]?.title}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          />
        )}
      </AnimatePresence>

      {/* Lớp phủ tối giúp chữ rõ hơn */}
      <div className="absolute inset-0 bg-black/40"></div>

      {/* Nội dung */}
      <div className="relative z-10 flex flex-col md:flex-row items-center justify-center md:justify-between gap-8 md:gap-0 max-w-7xl mx-auto w-full px-4 md:px-12 pt-32 pb-16 md:py-32">
        {/* ====== Cột chữ bên trái ====== */}
        <div className="w-full md:w-1/2 text-center md:text-left">
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 md:mb-6 text-white leading-tight"
            style={{
              textShadow:
                "0 0 8px rgba(0,0,0,0.6), 0 0 12px rgba(0,0,0,0.4)",
            }}
          >
            {banners[index]?.title || "Sofa L - Mã SL2025"}
          </h1>
          <p className="text-base md:text-lg mb-8 text-white/90 leading-relaxed max-w-md mx-auto md:mx-0">
            {banners[index]?.subtitle ||
              "Thiết kế sang trọng, tinh tế, tôn vinh không gian sống hiện đại."}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center md:justify-start">
            <a
              href={banners[index]?.buttonLink || DEFAULT_BANNER.buttonLink}
              onClick={handlePrimaryCta}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-[var(--color-primary)] px-6 py-3 font-semibold !text-white shadow-lg transition hover:bg-[#805b42] sm:px-7"
            >
              {banners[index]?.buttonText || DEFAULT_BANNER.buttonText}
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href="https://m.me/cunghome21"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContactClick("Messenger banner CTA")}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md border border-white/70 bg-black/20 px-6 py-3 font-semibold !text-white backdrop-blur-sm transition hover:border-white hover:bg-white hover:!text-gray-900 sm:px-7"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              Tư vấn qua fanpage
            </a>
          </div>
        </div>

        {/* ====== Cột ảnh bên phải ====== */}
        <div className="w-full md:w-1/2 mt-4 md:mt-0 flex justify-center md:justify-end">
          <div className="inline-block bg-white/10 backdrop-blur-md rounded-3xl p-3 md:p-4 shadow-2xl relative w-full max-w-[420px]">
            <AnimatePresence mode="wait">
              <motion.img
                key={banners[index]?.image}
                src={getOptimizedImageUrl(
                  banners[index]?.imageSmall || banners[index]?.image,
                  { width: 640 }
                )}
                srcSet={getResponsiveImageSrcSet(
                  banners[index]?.imageSmall || banners[index]?.image,
                  [360, 520, 720]
                )}
                sizes="(max-width: 767px) calc(100vw - 32px), 420px"
                alt={banners[index]?.title}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                className="w-full h-[220px] sm:h-[260px] md:h-[280px] object-cover rounded-2xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
              />
            </AnimatePresence>

            {banners.length > 1 && (
              <div className="absolute bottom-2 left-1/2 z-30 flex -translate-x-1/2 gap-0 md:bottom-3">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Chọn banner ${i + 1}`}
                    onClick={() => setIndex(i)}
                    className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full !bg-transparent !p-0 !shadow-none"
                  >
                    <span
                      className={`h-2.5 w-2.5 rounded-full transition-all duration-300 md:h-3 md:w-3 ${
                        i === index
                          ? "scale-125 bg-[var(--color-primary)] shadow-lg"
                          : "bg-white/50 hover:bg-white/80"
                      }`}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
